import React, { useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Check, ChevronsUpDown, X } from 'lucide-react';

/**
 * Client and team-member filters for the entry tables.
 *
 * Asked for once the approvals queue passed 200 entries and people could no
 * longer find their own clients in it. Both lists the filters apply to are
 * already complete in the browser - neither endpoint pages - so narrowing them
 * here is exact and costs no round trip, and it cannot disagree with what the
 * server would have returned for the same question.
 *
 * Options come from the rows themselves rather than from the client list, so
 * every choice offered has something behind it and carries its own count.
 */

// The generated TimeEntry types mark the client fields optional, though every
// entry belongs to a project and every project to a client.
type FilterableRow = {
  clientId?: number | null;
  clientName?: string | null;
  userId: number;
  userName: string;
};

export type EntryFilterState = { clientId: number | null; userId: number | null };

export const NO_ENTRY_FILTER: EntryFilterState = { clientId: null, userId: null };

export function applyEntryFilter<T extends FilterableRow>(rows: T[], f: EntryFilterState): T[] {
  return rows.filter(r =>
    (f.clientId === null || r.clientId === f.clientId)
    && (f.userId === null || r.userId === f.userId),
  );
}

type Option = { id: number; label: string; count: number };

function optionsFrom<T>(
  rows: T[],
  id: (r: T) => number | null | undefined,
  label: (r: T) => string | null | undefined,
): Option[] {
  const byId = new Map<number, Option>();
  for (const r of rows) {
    const key = id(r);
    if (key == null) continue;
    const existing = byId.get(key);
    if (existing) existing.count++;
    else byId.set(key, { id: key, label: label(r) ?? `#${key}`, count: 1 });
  }
  return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
}

function FilterPicker({
  noun,
  options,
  value,
  onChange,
}: {
  noun: string;
  options: Option[];
  value: number | null;
  onChange: (id: number | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.id === value);

  // Approving the last entry for the chosen client removes it from the
  // options while it is still the filter. Keep showing its name so the empty
  // table reads as "nothing left for Brookfield", not as an unfiltered page.
  const lastLabel = useRef<string | null>(null);
  if (selected) lastLabel.current = selected.label;
  const shown = selected?.label ?? (value !== null ? lastLabel.current : null);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={`Filter by ${noun}`}
          className={cn(
            'h-10 w-[200px] justify-between bg-card px-3 font-normal',
            value !== null && 'border-primary/50 text-primary',
          )}
        >
          <span className="truncate text-sm">
            {shown ?? <span className="text-muted-foreground">All {noun}s</span>}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder={`Search ${noun}s...`} />
          <CommandEmpty>No {noun}s found.</CommandEmpty>
          <CommandGroup>
            <ScrollArea className="max-h-64">
              <CommandItem
                value={`__all__ All ${noun}s`}
                onSelect={() => { onChange(null); setOpen(false); }}
              >
                <Check className={cn('mr-2 h-4 w-4', value === null ? 'opacity-100' : 'opacity-0')} />
                All {noun}s
              </CommandItem>
              {options.map(o => (
                <CommandItem
                  key={o.id}
                  // cmdk keys items by value, so two clients sharing a name
                  // would otherwise collapse into one.
                  value={`${o.label} #${o.id}`}
                  onSelect={() => { onChange(o.id); setOpen(false); }}
                >
                  <Check className={cn('mr-2 h-4 w-4 shrink-0', value === o.id ? 'opacity-100' : 'opacity-0')} />
                  <span className="truncate">{o.label}</span>
                  <span className="ml-auto pl-2 font-mono text-xs text-muted-foreground tabular-nums">{o.count}</span>
                </CommandItem>
              ))}
            </ScrollArea>
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function EntryFilters<T extends FilterableRow>({
  rows,
  value,
  onChange,
}: {
  rows: T[];
  value: EntryFilterState;
  onChange: (next: EntryFilterState) => void;
}) {
  // Each list is counted against the other filter, so picking a client leaves
  // only the people who logged time to it, and the counts say how many each
  // would show. A choice that would empty the table is never offered.
  const clientOptions = useMemo(
    () => optionsFrom(applyEntryFilter(rows, { ...value, clientId: null }), r => r.clientId, r => r.clientName),
    [rows, value],
  );
  const userOptions = useMemo(
    () => optionsFrom(applyEntryFilter(rows, { ...value, userId: null }), r => r.userId, r => r.userName),
    [rows, value],
  );

  // Somebody who can only see their own time has nobody to filter by.
  const showUsers = userOptions.length > 1 || value.userId !== null;
  const active = value.clientId !== null || value.userId !== null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <FilterPicker
        noun="client"
        options={clientOptions}
        value={value.clientId}
        onChange={clientId => onChange({ ...value, clientId })}
      />
      {showUsers && (
        <FilterPicker
          noun="team member"
          options={userOptions}
          value={value.userId}
          onChange={userId => onChange({ ...value, userId })}
        />
      )}
      {active && (
        <Button
          variant="ghost"
          size="sm"
          className="h-10 px-2.5 text-muted-foreground hover:text-foreground"
          onClick={() => onChange(NO_ENTRY_FILTER)}
        >
          <X className="mr-1 h-3.5 w-3.5" />
          Clear
        </Button>
      )}
    </div>
  );
}
